import apiClient from './apiClient';

// ============================================================
// Types — matching backend Go models (exchange_rate)
// ============================================================

export interface FINExchangeRate {
  f10e_01: string;          // 幣別代號
  f10e_02: string | null;   // 匯率日期
  f10e_12: number;          // 預估匯率
  f10e_11: number;          // 進口報關匯率
  f10e_13: number;          // 即期匯率(押匯)
  f10e_14: number;          // 財務匯率
  exp_customs_rate: number; // 出口報關匯率
  is_active: boolean;       // 啟用
  is_valid: boolean;        // 無效
  f10e_91: string;          // 建檔人員
  cdate: string | null;     // 建檔日期
  uuser: string;            // 修改人員
  udate: string | null;     // 修改日期
  user_name: string;        // 建檔人員姓名 (JOIN)
  rate_unit_value: number;  // 單位匯率比值 (JOIN)
}

export interface FINCurr {
  curr_code: string;
  ccurr_name: string;
  ecurr_name: string;
  rate_unit_value: number;
  is_active: boolean;
  is_valid: boolean;
}

export interface ACCSetting {
  setting_id: number;
  estimated_web: string;
  negotiation_web: string;
  customs_web: string;
}

export interface LatestRateResponse {
  f10e_12: number;          // 計算後預估匯率
  f10e_11: number;          // 前次進口報關匯率
  f10e_13: number;          // 前次即期匯率
  exp_customs_rate: number; // 前次出口報關匯率
}

export interface CreateRequest {
  f10e_01: string;
  f10e_02: string;
  f10e_12: number;
  f10e_11: number;
  f10e_13: number;
  f10e_14: number;
  exp_customs_rate: number;
  is_active?: boolean;
  is_valid?: boolean;
}

export interface UpdateRequest {
  f10e_12?: number;
  f10e_11?: number;
  f10e_13?: number;
  f10e_14?: number;
  exp_customs_rate?: number;
  is_active?: boolean;
  is_valid?: boolean;
}

export interface ListResponse {
  data: FINExchangeRate[];
  total: number;
  page: number;
  size: number;
}

export interface ValidationError {
  field: string;
  message: string;
}

// ============================================================
// Helpers
// ============================================================

function compositeId(currCode: string, rateDate: string): string {
  return `${currCode}_${rateDate}`;
}

// ============================================================
// API methods
// ============================================================

export const exchangeRateApi = {
  async list(page = 1, size = 20, currency?: string, date?: string): Promise<ListResponse> {
    const params: Record<string, string | number> = { page, size };
    if (currency) params.currency = currency;
    if (date) params.date = date;
    const res = await apiClient.get('/api/exchange-rate', { params });
    return res.data;
  },

  async get(currCode: string, rateDate: string): Promise<FINExchangeRate> {
    const id = compositeId(currCode, rateDate);
    const res = await apiClient.get(`/api/exchange-rate/${encodeURIComponent(id)}`);
    return res.data;
  },

  async create(data: CreateRequest): Promise<FINExchangeRate> {
    const res = await apiClient.post('/api/exchange-rate', data);
    return res.data;
  },

  async update(currCode: string, rateDate: string, data: UpdateRequest): Promise<FINExchangeRate> {
    const id = compositeId(currCode, rateDate);
    const res = await apiClient.put(`/api/exchange-rate/${encodeURIComponent(id)}`, data);
    return res.data;
  },

  async delete(currCode: string, rateDate: string): Promise<void> {
    const id = compositeId(currCode, rateDate);
    await apiClient.delete(`/api/exchange-rate/${encodeURIComponent(id)}`);
  },

  // BR-009: Get latest rate for auto-fill
  async getLatestRate(currCode: string): Promise<LatestRateResponse> {
    const res = await apiClient.get(`/api/exchange-rate/latest-rate/${encodeURIComponent(currCode)}`);
    return res.data;
  },

  // BR-016: List currencies (active + valid)
  async listCurrencies(): Promise<FINCurr[]> {
    const res = await apiClient.get('/api/exchange-rate/currencies');
    return res.data.data || [];
  },

  // BR-014: Get reference URLs
  async getSettings(): Promise<ACCSetting> {
    const res = await apiClient.get('/api/exchange-rate/settings');
    return res.data;
  },
};
